"use client"

import type { TrackEventModules, TrackingContext } from "@/modules/tracking/types"
import type { FunctionComponent, MouseEvent } from "react"

import { useState } from "react"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { HeartFilled } from "@jobteaser/spark/components/Icons/HeartFilled"
import { HeartOutlined } from "@jobteaser/spark/components/Icons/HeartOutlined"

import { setJobAdLikedState } from "@/modules/jobAds/client/api/setJobAdLikedState"
import { useNotification } from "@/modules/notification/hooks"
import { useTracking } from "@/modules/tracking/useTracking"

export type ErrorTranslations = {
  content: string
  title: string
}

type SaveJobAdButtonProps = {
  className: string
  errorTranslation?: ErrorTranslations
  isJobAdSaved?: boolean
  jobAdId: string
  trackEventModule: TrackEventModules
  trackingContext: TrackingContext
  title?: string
  unSaveLabel: string
  saveLabel: string
}

export const SaveJobAdButton: FunctionComponent<SaveJobAdButtonProps> = ({
  className,
  errorTranslation,
  isJobAdSaved,
  jobAdId,
  trackEventModule,
  trackingContext,
  title,
  unSaveLabel,
  saveLabel,
}) => {
  const tracking = useTracking()
  const showNotification = useNotification()

  const [isJobAdLiked, setIsJobAdLiked] = useState(isJobAdSaved || false)
  const handleSave = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.stopPropagation()

    try {
      await setJobAdLikedState({
        hostname: window.location.hostname,
        id: jobAdId,
        like: !isJobAdLiked,
      })

      setIsJobAdLiked(!isJobAdLiked)
      tracking?.track(isJobAdLiked ? "job_ad_dismiss_click" : "job_ad_save", {
        context: trackingContext,
        job_ad_id: jobAdId,
        module: trackEventModule,
        service: "jobteaser",
      })
    } catch {
      if (errorTranslation) {
        showNotification({
          content: errorTranslation.content,
          title: errorTranslation.title,
          variant: "error",
        })
      }
    }
  }

  return (
    <IconButton
      aria-label={isJobAdLiked ? unSaveLabel : saveLabel}
      className={className}
      data-testid={`heart-${isJobAdLiked ? "filled" : "outlined"}`}
      title={title}
      icon={isJobAdLiked ? HeartFilled : HeartOutlined}
      onClick={handleSave}
    />
  )
}
